import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { EmailFrequency } from '../../../../../enums/EmailFrecuency';
import '../../../../../scss/colors.scss';
import {ATTENDANCE_COLOR_MODEL, ATTENDANCE_SUBVIEW_MODEL} from '../Model/Model';
import HandleChange from '../../../../../Helpers/HandleChange';
import {ATTENDANCE_CYCLE, EMAIL_MODEL_DATA, EMAIL_VALUE_VALIDATE} from '../Manage/Settings/Model/Models';
import {
    errorMsg,
    getTimeFormat,
    userListToLabelEmailPhoneValue
} from '../../../../../Apis/functions';
import {
    GetAttendanceColor, GetAttendanceCycleConfig, ResetToDefaultAttendanceColor,
    UpdateAttendanceColor,
    UpdateAttendanceCycleConfig
} from '../../../../../reduxToolkit/AttendanceConfig/AttendanceConfigApi';
import {
    GetAllOnlyUserData,
    GetEmailSetting,
    UpdateEmailSettingAttendanceManage, updateUserEmailByList
} from '../../../../../reduxToolkit/EmployeePayRoll/EmployeePayRollApi';
import { PersonChipBlack } from '../../../../../components/buttons/IconButtons';
import FrequencyRange from './FrequencyRange';
import { PERIOD_TIME_UNIT_ENUM } from '../../../../../enums/PeriodTimeUnitEnum';

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle: Full Stack Developer - IBL
 * @Email: clopez@ibl.mx
 *
 *  * Hook/container function that centralizes the “Manage Attendance Settings” logic. It is responsible for:
 * - Loading initial settings (colors, cycle, email, and users) from Redux on mount.
 * - Re-loading data whenever changes are detected (via the `modified` flag).
 * - Validating email settings based on the selected frequency.
 * - Updating all settings (colors, cycle, email, and user list) in parallel.
 * - Registering the `handleUpdate` action via `registerAction` so the parent view can trigger it.
 *
 * @param {Function} registerAction Function used to register view/subview actions.
 * @returns {Object} State and handlers required by the UI: settings models, user list, flags, and change handlers.
 *
 */
export const ManageAttendanceFunctionality = (
    {
        registerAction
    }
) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { buildFrequencyRange } = FrequencyRange();

    const [emptyFields, setEmptyFields] = useState(false);
    const [modified, setModified] = useState(false);

    const [item, setItem] = useState({ ...ATTENDANCE_COLOR_MODEL.setDefaultData() });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    const [emailItem, setEmailItem] = useState({ ...EMAIL_MODEL_DATA });
    const { handleChange: handleChangeEmail } = HandleChange({ model: emailItem, setModel: setEmailItem });

    const [cycleItem, setCycleItem] = useState({ ...ATTENDANCE_CYCLE });
    const { handleChange: handleChangeCycle } = HandleChange({ model: cycleItem, setModel: setCycleItem });

    const [user, setUser] = useState([]);

    /**
     * Triggers the initial loading of all required data for the view
     * when the component is mounted. It runs only once.
     */
    useEffect(() => {
        getAllData()
    }, [])

    /**
     * Watches the `modified` state to detect configuration changes.
     * When it becomes true, it reloads all updated data
     * and resets the state to prevent repeated executions.
     */
    useEffect(() => {
        if (modified) {
            getAllData();
            setModified(false);
        }
    }, [modified])


    /**
     * Dynamically validates numeric fields of the attendance cycle configuration.
     * Whenever any of the observed values changes, the corresponding validation flags
     * are updated to indicate whether the values are greater than or equal to 1.
     */
    useEffect(() => {
        setCycleItem(prev => ({
            ...prev,
            isValidSupervisorReviewPeriodFrom: Number(prev.supervisorReviewPeriodFrom) >= 1,
            isValidAdminApprovalPeriodFrom: Number(prev.adminApprovalPeriodFrom) >= 1,
            isValidFrequencyValue: Number(prev.frequencyValue) >= 1,
            isValidCheckOutCooldown: Number(prev.checkOutCooldown) >= 1,
        }));
    }, [cycleItem.supervisorReviewPeriodFrom, cycleItem.adminApprovalPeriodFrom, cycleItem.frequencyValue, cycleItem.checkOutCooldown]);

    /**
     * Dynamically calculates and updates the attendance cycle time ranges
     * (frequency, supervisor review, and admin approval) when their base values change.
     * If no valid ranges are generated, no update is performed.
     */
    useEffect(() => {
        const frequency = buildFrequencyRange(cycleItem?.frequencyValue, cycleItem?.frequencyTimeUnit);
        const supervisor = buildFrequencyRange(cycleItem?.supervisorReviewPeriodFrom, PERIOD_TIME_UNIT_ENUM.DAYS.name);
        const admin = buildFrequencyRange(cycleItem?.adminApprovalPeriodFrom, PERIOD_TIME_UNIT_ENUM.DAYS.name);

        if (!frequency && !supervisor && !admin) return;

        setCycleItem((prev) => ({
            ...prev,

            ...(frequency && {
                frequencyStart: frequency.start,
                frequencyEnd: frequency.end,
                frequencyTime: getTimeFormat(frequency.endMs),
            }),

            ...(supervisor && {
                supervisorStart: supervisor.start,
                supervisorEnd: supervisor.end,
            }),

            ...(admin && {
                adminStart: admin.start,
                adminEnd: admin.end,
            }),
        }));
    }, [cycleItem?.frequencyValue, cycleItem?.frequencyTimeUnit, cycleItem?.supervisorReviewPeriodFrom, cycleItem?.adminApprovalPeriodFrom]);

    /**
     * Registers the `handleUpdate` action for the attendance settings subview.
     * The action is re-registered whenever relevant state changes,
     * ensuring the view always has access to the most up-to-date
     * version of the update handler.
     */
    useEffect(() => {
        registerAction(
            ATTENDANCE_SUBVIEW_MODEL.MANAGE.SETTINGS,
            { handleUpdate }
        );
    }, [item, emailItem, cycleItem, user, emptyFields]);

    /**
     * Fetches and loads all data required for the attendance configuration.
     * Executes multiple Redux actions in parallel and updates the corresponding state
     * when each one completes successfully. If an error occurs, the related message is shown.
     */
    const getAllData = () => {
        const run = async (action, onSuccess) => {
            const res = await dispatch(action);
            const { data, success, code } = res?.payload?.data ?? {};
            if (!success) throw code;

            onSuccess?.(data);
            return data;
        };

        (async () => {
            try {
                await Promise.all([
                    run(GetAttendanceColor(), (data) => setItem((prev) => prev.setData(data))),
                    run(GetAttendanceCycleConfig(), (data) => setCycleItem((prev) => prev.setData(data))),
                    run(GetEmailSetting(), (data) => setEmailItem((prev) => prev.setData(data))),
                    run(GetAllOnlyUserData(), (data) => setUser(userListToLabelEmailPhoneValue(data, "id", true, PersonChipBlack))),
                ]);
            } catch (code) {
                errorMsg(code);
            }
        })();
    };

    /**
     * Validates the email notification configuration based on the selected frequency.
     * Ensures that required fields are present and their values are valid
     * according to the frequency type (daily, weekly, monthly, or biweekly).
     * @returns {boolean}
     */
    const isValidEmailItem = () => {
        if (!emailItem?.reviewFrequency) return false;

        switch (emailItem.reviewFrequency) {
            case EmailFrequency.DAILY.value:
                return emailItem?.daysAfterSendEmail !== '' && Number(emailItem.daysAfterSendEmail) >= 0;

            case EmailFrequency.WEEKLY.value:
                return emailItem?.dayOfWeek !== '' && Number(emailItem.dayOfWeek) >= 1;

            case EmailFrequency.MONTHLY.value:
                return emailItem?.dayOfMonth !== '' && Number(emailItem.dayOfMonth) >= 1;

            case EmailFrequency.BIWEEKLY.value: {
                const start = Number(emailItem.startDate);
                const end = Number(emailItem.endDate);
                return (
                    !isNaN(start) &&
                    !isNaN(end) &&
                    start >= EMAIL_VALUE_VALIDATE.start.min &&
                    start <= EMAIL_VALUE_VALIDATE.start.max &&
                    end >=  EMAIL_VALUE_VALIDATE.end.min &&
                    end <= EMAIL_VALUE_VALIDATE.end.max
                );
            }
            default:
                return false;
        }
    };

    /**
     * Executes the update of the entire attendance configuration.
     * Before submitting the data, it validates the email settings
     * and the attendance cycle state. If validation fails, a warning
     * message is displayed. When the update succeeds, it marks the
     * state as modified and notifies the user.
     */
    const handleUpdate = async () => {
        if (!isValidEmailItem() || !cycleItem.isValid()) {
            setEmptyFields(true);
            toast.warn(t("please_fill_all_fields").toUpperCase());
            return;
        }

        const palletBody = item.toRegister();
        const cycleBody = cycleItem.toRegister();
        const emailBody = emailItem.toRegister();
        const userIds = Array.from(
            new Set((user ?? []).map(x => x?.userId).filter(Boolean))
        );

        const run = async (action) => {
            const response = await dispatch(action);
            const { success, code } = response?.payload?.data ?? {};
            if (!success) throw code;
            return true;
        };

        try {
            await Promise.all([
                run(UpdateAttendanceColor(palletBody)),
                run(UpdateAttendanceCycleConfig(cycleBody)),
                run(UpdateEmailSettingAttendanceManage(emailBody)),
                run(updateUserEmailByList(userIds)),
            ]);

            setModified(true);
            toast.success(t('has_been_updated_successfully'));
        } catch (code) {
            errorMsg(code);
        }
    };

    const resetToDefaultPallet = async () => {
        dispatch(ResetToDefaultAttendanceColor()).then(({ payload: { data: { data, success, code } } }) => {
            if(success){
                setModified(true);
                toast.success(t('has_been_updated_successfully'));
            }else errorMsg(code)
        });
    }

    return {
        emptyFields,
        user, setUser,
        item,
        handleChange,
        emailItem,
        handleChangeEmail,
        cycleItem,
        handleChangeCycle,
        resetToDefaultPallet
    };
};
